import { useT } from '../context/I18nContext';
import { formatDate } from '../lib/dates';
import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { formatPaise } from '../lib/money';
import { useNavigate } from 'react-router-dom';

/* ============================================================================
   THE FAMILY HOME

   Replaces the screen where a parent used to be shown a school-wide fee figure
   they had no permission to see — producing a 403 and the impression that the
   app was broken.

   Everything here is one API call, because a parent on a patchy connection
   should not wait for eight.
   ========================================================================== */

interface BusStatus {
  routeName: string; vehicleName: string | null; stopName: string | null;
  status: string; departedAt: string | null;
}
interface LatestResult { examName: string; subjectName: string; marks: number | null; grade: string | null; }
interface Period { periodNumber: number; startTime: string; subjectName: string; teacherName: string | null; roomName: string | null; }
interface AbsentTeacher { teacherName: string; subjectName: string; periodNumber: number; }
interface RequestSummary { id: string; type: string; title: string; status: string; submittedAt: string; }

interface Child {
  studentId: string; fullName: string; admissionNumber: string; classLabel: string | null;
  attendancePercent: number | null; presentDays: number; markedDays: number; todayStatus: string | null;
  feesDuePaise: number; nextDueDate: string | null; feesOverdue: boolean;
  bus: BusStatus | null;
  latestResult: LatestResult | null;
  todayTimetable: Period[];
  absentTeachers: AbsentTeacher[];
  openRequests: RequestSummary[];
}
interface FamilyHome { greeting: string; children: Child[]; totalFeesDuePaise: number; }

export function FamilyHomePage() {
  const t = useT();
  const [home, setHome] = useState<FamilyHome | null>(null);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get<FamilyHome>('/api/portal/home')
      .then(setHome)
      .catch(() => setError('We couldn’t load your home screen. Pull down to try again.'));
  }, []);

  if (error) {
    return <div className="empty"><h3>{t('Something went wrong')}</h3><p>{error}</p></div>;
  }

  if (!home) {
    return (
      <div>
        <span className="sk" style={{ width: 200, height: 28, marginBottom: 24 }} />
        <div className="card" style={{ padding: 20 }}>
          <span className="sk" style={{ width: '40%', marginBottom: 12 }} />
          <span className="sk" style={{ width: '70%', marginBottom: 8 }} />
          <span className="sk" style={{ width: '55%' }} />
        </div>
      </div>
    );
  }

  // No child linked yet — a real state, and it needs a real answer, not an error.
  if (home.children.length === 0) {
    return (
      <div>
        <h1 className="h-display" style={{ fontSize: 28, marginBottom: 8 }}>{home.greeting}</h1>
        <div className="empty">
          <h3>{t('No child linked yet')}</h3>
          <p>
            Ask the school office to link your child to this account — or enter the code they gave you.
          </p>
          <button className="btn btn-accent" onClick={() => navigate('/family')}>{t('Link a child')}</button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <h1 className="h-display" style={{ fontSize: 28, marginBottom: 4 }}>{home.greeting}</h1>
      <p style={{ color: 'var(--ink-2)', marginBottom: 24 }}>
        {home.totalFeesDuePaise > 0
          ? <>You have <strong>{formatPaise(home.totalFeesDuePaise)}</strong> in fees due.</>
          : <>{t('All fees are paid. Nothing outstanding.')}</>}
      </p>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
        {home.children.map((c) => <ChildCard key={c.studentId} child={c} onNavigate={navigate} />)}
      </div>
    </div>
  );
}

function ChildCard({ child: c, onNavigate }: { child: Child; onNavigate: (to: string) => void }) {
  const t = useT();
  const absentToday = c.todayStatus === 'Absent';

  return (
    <article className="card" style={{ padding: 0, overflow: 'hidden' }}>
      {/* ---- header ---- */}
      <div style={{ padding: '18px 20px', borderBottom: '1px solid var(--line-2)' }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
          <h2 className="h-display" style={{ fontSize: 20 }}>{c.fullName}</h2>
          <span className="mono" style={{ fontSize: 12, color: 'var(--ink-3)' }}>{c.admissionNumber}</span>
          {c.classLabel && <span className="pill pill-muted" style={{ fontSize: 11 }}>{c.classLabel}</span>}
          {c.todayStatus && (
            <span
              className={`pill ${c.todayStatus === 'Present' ? 'pill-ok' : absentToday ? 'pill-danger' : 'pill-muted'}`}
              style={{ fontSize: 11, marginLeft: 'auto' }}
            >
              {absentToday ? 'Absent today' : `${c.todayStatus} today`}
            </span>
          )}
        </div>
      </div>

      {/* ---- the two numbers that matter ---- */}
      <div className="kpis" style={{ margin: 0, padding: '16px 20px', gap: 12 }}>
        <button
          className="card"
          style={{ padding: 14, textAlign: 'left', cursor: 'pointer', background: 'var(--surface-2)' }}
          onClick={() => onNavigate('/family')}
        >
          <div style={{ fontSize: 11, color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: '.04em' }}>
            Attendance this month
          </div>
          <div style={{ fontSize: 26, fontWeight: 700, fontFamily: 'var(--font-display)' }}>
            {c.attendancePercent === null ? '—' : `${c.attendancePercent}%`}
          </div>
          <div style={{ fontSize: 12, color: 'var(--ink-3)' }}>
            {c.markedDays === 0 ? 'Not marked yet' : `${c.presentDays} of ${c.markedDays} days`}
          </div>
        </button>

        <button
          className="card"
          style={{ padding: 14, textAlign: 'left', cursor: 'pointer', background: 'var(--surface-2)' }}
          onClick={() => onNavigate('/family')}
        >
          <div style={{ fontSize: 11, color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: '.04em' }}>
            Fees due
          </div>
          <div style={{ fontSize: 26, fontWeight: 700, fontFamily: 'var(--font-display)',
                        color: c.feesDuePaise > 0 ? 'var(--brand)' : undefined }}>
            {c.feesDuePaise > 0 ? formatPaise(c.feesDuePaise) : 'Paid'}
          </div>
          {c.feesDuePaise > 0 && (
            <div style={{ fontSize: 12, color: c.feesOverdue ? 'var(--danger-ink, #b3261e)' : 'var(--ink-3)' }}>
              {c.feesOverdue ? 'Overdue' : c.nextDueDate ? `Due ${formatDate(c.nextDueDate)}` : ''}
            </div>
          )}
        </button>
      </div>

      {/* ---- bus ---- */}
      {c.bus && (
        <Row icon="🚌">
          <strong>{c.bus.routeName}</strong>
          {c.bus.status === 'Departed' ? (
            <span style={{ color: 'var(--ok-ink, #1e7d4f)' }}>
              {' '}departed{c.bus.departedAt
                ? ` at ${new Date(c.bus.departedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
                : ''}
            </span>
          ) : (
            <span style={{ color: 'var(--ink-3)' }}> — not departed yet</span>
          )}
          {c.bus.stopName && <div style={{ fontSize: 12, color: 'var(--ink-3)' }}>{t('Stop')}: {c.bus.stopName}</div>}
        </Row>
      )}
      {absentToday && (
        <Row icon="🚌">
          <span style={{ color: 'var(--ink-3)' }}>
            No bus updates — {c.fullName.split(' ')[0]} is marked absent today.
          </span>
        </Row>
      )}

      {/* ---- absent teachers: the thing parents actually wonder ---- */}
      {c.absentTeachers.length > 0 && (
        <Row icon="⚠">
          <strong>{t('Teacher absent today')}</strong>
          {c.absentTeachers.map((t) => (
            <div key={`${t.periodNumber}-${t.subjectName}`} style={{ fontSize: 13, color: 'var(--ink-2)' }}>
              {t.teacherName} · {t.subjectName} (period {t.periodNumber})
            </div>
          ))}
        </Row>
      )}

      {/* ---- today's timetable ---- */}
      {c.todayTimetable.length > 0 && (
        <Row icon="🕘">
          <strong>{t('Today’s classes')}</strong>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 }}>
            {c.todayTimetable.map((p) => (
              <span key={p.periodNumber} className="pill pill-muted" style={{ fontSize: 11 }}>
                {p.startTime} {p.subjectName}
              </span>
            ))}
          </div>
        </Row>
      )}

      {/* ---- latest result ---- */}
      {c.latestResult && (
        <Row icon="📄">
          <strong>{c.latestResult.examName}</strong>
          <div style={{ fontSize: 13, color: 'var(--ink-2)' }}>
            {c.latestResult.subjectName}
            {c.latestResult.marks !== null && ` · ${c.latestResult.marks} marks`}
            {c.latestResult.grade && ` · ${c.latestResult.grade}`}
          </div>
        </Row>
      )}

      {/* ---- open requests ---- */}
      {c.openRequests.length > 0 && (
        <Row icon="📮">
          <strong>{t('Awaiting the school’s reply')}</strong>
          {c.openRequests.map((r) => (
            <div key={r.id} style={{ fontSize: 13, color: 'var(--ink-2)' }}>{r.title} · {r.status}</div>
          ))}
        </Row>
      )}

      {/* ---- actions ---- */}
      <div style={{ display: 'flex', gap: 8, padding: '14px 20px', borderTop: '1px solid var(--line-2)' }}>
        {c.feesDuePaise > 0 && (
          <button className="btn btn-accent" onClick={() => onNavigate('/family')}>{t('Pay fees')}</button>
        )}
        <button className="btn" onClick={() => onNavigate('/family')}>{t('Raise a request')}</button>
      </div>
    </article>
  );
}

function Row({ icon, children }: { icon: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', gap: 12, padding: '12px 20px', borderTop: '1px solid var(--line-2)' }}>
      <span aria-hidden="true" style={{ fontSize: 15, lineHeight: 1.4 }}>{icon}</span>
      <div style={{ minWidth: 0, flex: 1 }}>{children}</div>
    </div>
  );
}
